import "../NewUpsell/css/newUpsell.scss";
import "../NewUpsell/css/animations.scss";
import "./css/v3.scss";

import { useEffect, useRef, useState } from "react";
import { Typography, GlobalIcon } from "get-life-storybook-ts";

import UpgradeCapitalModal from "../NewUpsell/Components/UpgradeCapitalModal/UpgradeCapitalModal";
import MaxCapitalModal from "../../components/MaxCapitalModal/MaxCapitalModal";
import ConditionsModalAB from "../../components/ConditionsModal/ConditionsModalAB";
import PendingCapitalAdjustmentModal from "../../components/PendingCapitalAdjustmentModal/PendingCapitalAdjustmentModal";

import V3Hero from "./Components/V3/V3Hero";
import V3CoverageCards from "./Components/V3/V3CoverageCards";
import V3CoverageScope from "./Components/V3/V3CoverageScope";
import V3TrustStats from "./Components/V3/V3TrustStats";
import V3Reviews from "./Components/V3/V3Reviews";
import V3FinalCTA from "./Components/V3/V3FinalCTA";
import V3StickyCTA from "./Components/V3/V3StickyCTA";

import UpsellController from "../NewUpsell/Upsell.controller";
import { QuestionTreeErrorPanel } from "../Questions/QuestionTreeError";
import { useUpsellDisplayData } from "../NewUpsell/hooks/useUpsellDisplayData";
import { PurchasePeriodT } from "../../api/request/Lead/Interfaces/DataInterface.response";

const formatStartDate = (raw?: string, locale?: string): string => {
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  const lang =
    locale === "fr"
      ? "fr-FR"
      : locale === "it"
      ? "it-IT"
      : locale === "pt"
      ? "pt-PT"
      : locale === "en"
      ? "en-GB"
      : "es-ES";
  return new Intl.DateTimeFormat(lang, {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
};

const formatCapitalText = (value: number): string => {
  return `${new Intl.NumberFormat(
    process.env.REACT_APP_DOMAIN_LOCALE === "fr" ? "fr-FR" : "es-ES"
  ).format(value)} €`;
};

const Upsell = () => {
  const {
    downloadDocument,
    handleUpgradedClose,
    priceChange,
    isMobile,
    selectPlan,
    translate,
    upGradedOpen,
    downloadableFiles,
    product,
    nodeConditionsModal,
    handleConditions,
    couponInfo,
    traitState,
    pendingAdjustmentModal,
    gtf_campaign,
    afterPromoText,
    beforePromoText,
    domain,
    pricesV3,
    questionTreeError,
    retryQuestionTree,
    isRetryingQuestionTree,
  } = UpsellController();

  const showCoverageDisclaimers = domain === "es" || domain === "wl_es";

  const TRAIT_LABELS: Record<string, string> = {
    DEATH: translate("trait.DEATH") || "Fallecimiento",
    DISABILITY: translate("trait.DISABILITY") || "Incapacidad",
    SERIOUS_ILLNESS: translate("trait.SERIOUS_ILLNESS") || "Enfermedad grave",
    MALATTIA_TERMINALE:
      translate("trait.MALATTIA_TERMINALE") || "Malattia terminale",
  };

  const { guaranteeOptions, handleGuaranteeToggle, handleCapitalChange } =
    useUpsellDisplayData({
      product,
      translate,
      traitLabels: TRAIT_LABELS,
      capitalsByTrait: traitState.capitalsByTrait,
      traitBoundaries: traitState.traitBoundaries,
      traitLoading: traitState.loading,
      isTraitActive: traitState.isTraitActive,
      toggleTrait: traitState.toggleTrait,
      setTraitCapital: traitState.setTraitCapital,
      beforePromoText,
      afterPromoText,
      couponInfo,
      handleConditions,
    });

  const finalCtaRef = useRef<HTMLDivElement>(null);
  const [finalCtaInView, setFinalCtaInView] = useState(false);

  useEffect(() => {
    const node = finalCtaRef.current;
    if (!node || !isMobile) return;
    const observer = new IntersectionObserver(
      ([entry]) => setFinalCtaInView(entry.isIntersecting),
      { threshold: 0.25 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isMobile, product]);

  if (null !== questionTreeError) {
    return (
      <QuestionTreeErrorPanel
        kind={questionTreeError}
        translate={translate}
        onRetry={retryQuestionTree}
        retrying={isRetryingQuestionTree}
      />
    );
  }

  if (!product) return null;

  const ctaLabel = translate("newUpsell.v3.priceCard.cta");

  const monthlyPrices = pricesV3?.monthly;
  const monthlyAmount =
    monthlyPrices?.promoted ??
    monthlyPrices?.real ??
    (product.promotion && product.priceToPay
      ? product.priceToPay
      : product.regularPrice ?? 0);

  const deathCap = traitState.capitalsByTrait["DEATH"] ?? 0;
  const disabilityCap = traitState.capitalsByTrait["DISABILITY"] ?? 0;
  const seriousIllnessCap = traitState.capitalsByTrait["SERIOUS_ILLNESS"] ?? 0;
  const heroCapital = Math.max(deathCap, disabilityCap) + seriousIllnessCap;
  const heroCapitalLabel = formatCapitalText(heroCapital);

  const priceFormatter = new Intl.NumberFormat(
    process.env.REACT_APP_DOMAIN_LOCALE === "fr" ? "fr-FR" : "es-ES",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 }
  );
  const heroPriceLabel = `${priceFormatter.format(monthlyAmount)}€`;

  const hasPromo = !!product.promotion;
  const heroSuffix =
    hasPromo && beforePromoText
      ? beforePromoText
      : translate("newUpsell.v3.hero.sentence.monthly");

  const breakdown = guaranteeOptions
    .filter((g) => g.active && g.capital !== undefined)
    .map((g) => ({
      label: g.label,
      value: formatCapitalText(g.capital ?? 0),
    }));

  const activeCount = guaranteeOptions.filter((g) => g.active).length;

  const startsLabel = formatStartDate(
    product.effectDate,
    process.env.REACT_APP_DOMAIN_LOCALE
  );

  return (
    <div className={`upsell-v3${isMobile ? " upsell-v3-mobile-padding" : ""}`}>
      {(gtf_campaign === "VIDAAUTONOMO" ||
        product?.promotion?.promotionalCode === "VIDAAUTONOMO") && (
        <div className="banner-autonomo w-full text-white py-4 px-6">
          <div className="max-w-[960px] mx-auto flex items-center gap-4">
            <GlobalIcon iconName="ShieldCheckIcon" size="L" color="white" />
            <div>
              <Typography
                variant="Body"
                weight="700"
                text={translate("upsell.banner.autonomo.title")}
                className="text-white"
              />
              <Typography
                variant="BodySmall"
                weight="400"
                text={translate("upsell.banner.autonomo.subtitle")}
                className="text-white opacity-90 mt-1"
              />
            </div>
          </div>
        </div>
      )}

      <V3Hero
        translate={translate}
        priceAmount={monthlyAmount}
        loading={priceChange}
        ctaLabel={ctaLabel}
        onCtaClick={selectPlan}
        breakdown={breakdown}
        startsLabel={startsLabel}
        downloadableFiles={downloadableFiles}
        downloadDocument={downloadDocument}
        beforePromoText={beforePromoText}
        afterPromoText={afterPromoText}
        onPromoClick={couponInfo ? handleConditions : undefined}
        heroCapitalLabel={heroCapitalLabel}
        heroPriceLabel={heroPriceLabel}
        heroSuffix={heroSuffix}
      />

      <V3CoverageCards
        translate={translate}
        guarantees={guaranteeOptions}
        onToggle={handleGuaranteeToggle}
        onCapitalChange={handleCapitalChange}
        totalAmount={monthlyAmount}
        loading={priceChange || traitState.loading}
        beforePromoText={beforePromoText}
        afterPromoText={afterPromoText}
        onPromoClick={couponInfo ? handleConditions : undefined}
        showDisclaimers={showCoverageDisclaimers}
      />

      <V3CoverageScope
        translate={translate}
        downloadableFiles={downloadableFiles}
        downloadDocument={downloadDocument}
      />

      <V3TrustStats translate={translate} />

      <V3Reviews translate={translate} />

      <V3FinalCTA
        ref={finalCtaRef}
        translate={translate}
        onClick={selectPlan}
        capitalLabel={heroCapitalLabel}
        priceLabel={heroPriceLabel}
        suffix={heroSuffix}
      />

      {isMobile && (
        <V3StickyCTA
          translate={translate}
          amount={monthlyAmount}
          loading={priceChange || traitState.loading}
          activeCount={activeCount}
          hidden={finalCtaInView}
          onClick={selectPlan}
        />
      )}

      {/* Modals — kept identical to preserve business logic */}
      <UpgradeCapitalModal
        upGradedOpen={upGradedOpen}
        handleUpgradedClose={handleUpgradedClose}
        capitalsByTrait={traitState.capitalsByTrait}
      />
      <MaxCapitalModal capital={product.capital} />
      <PendingCapitalAdjustmentModal {...pendingAdjustmentModal} />
      {product.promotion && (
        <ConditionsModalAB
          couponInfo={couponInfo}
          nodeFakeDoorModal={nodeConditionsModal}
          promotion={product.promotion as any}
          collection={product.feeCollection as any}
          period={
            (product.period?.toLowerCase() || "monthly") as PurchasePeriodT
          }
          handleSubmit={() => nodeConditionsModal.current?.handleShowPortal()}
        />
      )}
    </div>
  );
};

export default Upsell;
